import { dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';

const PREFIX = 'lift-data-v1:';
// Cached screens show at once and refresh in the background, so a week-old feed beats a blank wait.
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const MAX_CHARS = 1_000_000; // At most ~2 MiB of UTF-16 local storage.
const PERSISTED = new Set(['account', 'feed', 'posts', 'post-neighbors', 'post-tags', 'discussion', 'discussion-thread', 'inbox', 'challenge', 'pledges', 'week-posts']);

export function accessDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { status?: number; code?: string };
  return e.status === 401 || e.status === 403 || e.code === '42501' || e.code === 'PGRST301';
}

// Persist display data only. Never store auth tokens, shipping/onboarding forms,
// admin review queues, permission queries, pending writes, or temporary blob URLs.
const saved = (posts: unknown) => Array.isArray(posts)
  ? posts.filter((post: { id?: unknown }) => !String(post?.id).startsWith('pending-')) : posts;
function displayData(key: unknown, data: unknown): unknown {
  // Posts still being published live only in memory (see features/feed/outbox).
  if (key === 'week-posts') return saved(data);
  if (key === 'posts' && data && typeof data === 'object' && Array.isArray((data as { pages?: unknown }).pages))
    return { ...data, pages: (data as { pages: unknown[] }).pages.map(saved) };
  if (key !== 'account') return data;
  const account = data as Record<string, any>;
  return {
    user: { id: account.user?.id },
    profile: account.profile,
    application: account.application ? { status: account.application.status, season_id: account.application.season_id } : null,
    season: account.season, teams: account.teams, admin: account.admin,
  };
}

export class QueryPersistence {
  private owner: string | null | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private unsubscribe: (() => void)[];
  constructor(private client: QueryClient, private storage: Storage) {
    const schedule = () => {
      if (!this.owner || this.timer) return;
      this.timer = setTimeout(() => { this.timer = undefined; this.flush(); }, 350);
    };
    this.unsubscribe = [client.getQueryCache().subscribe(schedule), client.getMutationCache().subscribe(schedule)];
  }

  setOwner(owner: string | null) {
    if (owner === this.owner) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.owner = undefined; // Don't persist cache-clear notifications under either account.
    this.client.clear();
    try {
      for (let i = this.storage.length - 1; i >= 0; i--) {
        const key = this.storage.key(i);
        if (key?.startsWith(PREFIX) && key !== PREFIX + owner) this.storage.removeItem(key);
      }
      if (owner) {
        const raw = this.storage.getItem(PREFIX + owner);
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved.owner === owner && Array.isArray(saved.queries)) {
            hydrate(this.client, { mutations: [], queries: saved.queries.filter((q: any) =>
              PERSISTED.has(q.queryKey?.[0]) && q.state?.dataUpdatedAt > Date.now() - MAX_AGE &&
              (q.queryKey[0] !== 'account' || q.state.data?.application?.status === 'approved'),
            ).map((q: any) => ({ ...q, state: { ...q.state, isInvalidated: true, fetchStatus: 'idle' } })) });
          }
        }
      }
    } catch { /* Corrupt, disabled, or full storage must not block startup. */ }
    this.owner = owner;
  }

  flush = () => {
    // An optimistic result isn't confirmed data and must not survive a restart.
    if (!this.owner || this.client.isMutating()) return;
    try {
      const state = dehydrate(this.client, {
        shouldDehydrateMutation: () => false,
        shouldDehydrateQuery: q => PERSISTED.has(String(q.queryKey[0])) && q.state.data !== undefined &&
          !accessDenied(q.state.error) && q.state.dataUpdatedAt > Date.now() - MAX_AGE &&
          (q.queryKey[0] !== 'account' || (q.state.data as any)?.application?.status === 'approved'),
      });
      const queries = state.queries.sort((a, b) =>
        Number(b.queryKey[0] === 'account') - Number(a.queryKey[0] === 'account') ||
        Number(b.queryKey[0] === 'feed') - Number(a.queryKey[0] === 'feed') ||
        b.state.dataUpdatedAt - a.state.dataUpdatedAt,
      ).map(q => ({ ...q, promise: undefined, state: { ...q.state,
        data: displayData(q.queryKey[0], q.state.data), error: null, fetchFailureReason: null,
        status: 'success' as const, fetchStatus: 'idle' as const,
      } }));
      const saved: typeof queries = [];
      let chars = 0;
      for (const query of queries) {
        const size = JSON.stringify(query).length;
        if (chars + size > MAX_CHARS) continue;
        chars += size; saved.push(query);
      }
      this.storage.setItem(PREFIX + this.owner, JSON.stringify({ owner: this.owner, queries: saved }));
    } catch { /* The live in-memory cache continues to work if persistence fails. */ }
  };

  dispose() {
    this.flush();
    if (this.timer) clearTimeout(this.timer);
    this.unsubscribe.forEach(stop => stop());
  }
}
